import { useState, useMemo } from "react";
import { FlexImage } from "@/components/FlexImage";
import config from "@/config.json";

export default function CataloguePage() {
  const categories = useMemo(() => {
    const cats = ["All", ...new Set(config.catalogue.map((p) => p.category))];
    return cats;
  }, []);

  const [activeCategory, setActiveCategory] = useState("All");

  const filtered = useMemo(() => {
    if (activeCategory === "All") return config.catalogue;
    return config.catalogue.filter((p) => p.category === activeCategory);
  }, [activeCategory]);

  // Bento grid sizing: first item and every 5th get larger
  const getGridClass = (index) => {
    if (index === 0) return "sm:col-span-2 sm:row-span-2";
    if (index % 5 === 3) return "sm:row-span-2";
    return "";
  };

  return (
    <main data-testid="catalogue-page" className="pt-20 bg-[#FDFCF8]">
      {/* Page Header */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F4F1EA]">
        <div className="max-w-[1600px] mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#C5A059] mb-4 font-body">
            Our Collection
          </p>
          <h1 className="font-heading text-5xl md:text-7xl font-medium tracking-tight text-[#1A1A1A] mb-6">
            Catalogue
          </h1>
          <p className="text-base md:text-lg text-[#595959] leading-relaxed font-body max-w-xl mx-auto">
            Browse our complete range of handcrafted artificial jewellery. Each piece is designed with love and attention to detail.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-12 px-6 md:px-12 lg:px-24 border-b border-[#E5E5E5]">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex flex-wrap items-center gap-3 justify-center" data-testid="category-filters">
            {categories.map((cat) => (
              <button
                key={cat}
                data-testid={`filter-${cat.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => setActiveCategory(cat)}
                className={`px-6 py-2.5 text-xs uppercase tracking-[0.2em] font-bold transition-all duration-300 cursor-pointer ${
                  activeCategory === cat
                    ? "bg-[#1A1A1A] text-white"
                    : "bg-transparent text-[#595959] border border-[#E5E5E5] hover:border-[#1A1A1A] hover:text-[#1A1A1A]"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Product Grid - Bento/Tetris Layout */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1600px] mx-auto">
          <div
            data-testid="product-grid"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-[280px] sm:auto-rows-[300px]"
          >
            {filtered.map((product, index) => (
              <div
                key={product.id}
                data-testid={`product-card-${product.id}`}
                className={`group relative overflow-hidden bg-white cursor-pointer ${getGridClass(index)} animate-slide-up`}
                style={{ animationDelay: `${index * 80}ms` }}
              >
                <FlexImage
                  src={product.image}
                  alt={product.name}
                  className="absolute inset-0 w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700 ease-in-out"
                />
                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                {/* Info on hover */}
                <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                  <span className="text-xs uppercase tracking-[0.15em] text-[#C5A059] font-bold font-body">
                    {product.category}
                  </span>
                  <h3 className="font-heading text-xl font-medium text-white mt-1">
                    {product.name}
                  </h3>
                  <p className="text-sm text-white/70 mt-1 font-body line-clamp-2">
                    {product.description}
                  </p>
                </div>
                {/* Category badge always visible */}
                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 group-hover:opacity-0 transition-opacity duration-300">
                  <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#1A1A1A] font-body">
                    {product.category}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {filtered.length === 0 && (
            <div data-testid="no-products-message" className="text-center py-24">
              <p className="text-lg text-[#595959] font-body">
                No products found in this category.
              </p>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
